// "POISTA DEMOTENANTTI" — the decisions behind the operator's permanent deletion of a demo tenant
// (task dc216a05, owner decision 1.11.2026, ADR-0023).
//
// WHY THIS IS A MODULE AND NOT A `switch` INSIDE THE COMPONENTS, which is the arrangement
// `tenantDemoConversion.ts` and `tenantRoleHolders.ts` already use in this directory. Every function below
// picks an i18n KEY or a yes/no from a WIRE VALUE, and almost none of those values is reachable by hand: six
// refusal codes, five obligation stores, a `404` that means something narrow, and a `done`/`incomplete`
// split where `incomplete` is the NORMAL ending of a successful deletion. Under `fallbackLng: "fi"` a wrong
// or missing key on any of those paths ships silently — which is what `src/i18n/platform-i18n.test.mjs`
// reads this file's literals to prevent, and what `tenant-deletion.test.mjs` exercises at states a browser
// run cannot easily be put into (ADR-0002 Layer 2 entry rule A).
//
// IT IMPORTS NOTHING AT RUNTIME, deliberately: that is what lets Node's own test runner load it under
// `--experimental-strip-types`, like its two neighbours.

/**
 * The problem-details shape these readers need. Declared structurally rather than imported from
 * `@web/lib/api`, for the zero-runtime-import reason in the header.
 */
export type DeletionProblem = { [key: string]: unknown } | undefined;

/** One store with work left on it — the wire shape of `TenantDeletionOutstandingDto`. */
export type Outstanding = { store: string; count: number | string; lastError?: string | null };

/**
 * `TenantDeletion.status` values, spelled here a SECOND time rather than imported from `tenantsApi.ts`'s
 * `DELETION_STATUS`.
 *
 * Importing it would pull the API client into this module and end the zero-runtime-import property the
 * header is about. The two spellings are pinned together by a test that reads the constant out of
 * `tenantsApi.ts` and out of the backend's own C#, so a drift fails there rather than quietly reporting a
 * deletion that still owes a crypto-shred as finished.
 */
export const STATUS_DONE = "done";
export const STATUS_INCOMPLETE = "incomplete";

/**
 * The obligation stores (`TenantDeletionStores`), spelled here for the same reason and pinned the same way.
 *
 * {@link STORE_KEYCLOAK_FEDERATION} is the one the console does not discharge: ADR-0014 §7.2 gives the
 * request-path API no call path to the realm, so the provisioning component removes the alias on its next
 * pass. See {@link consoleCanRetry}, which is the whole reason this constant is named rather than inlined.
 */
export const STORE_TRANSIT_KEY = "transit_key";
export const STORE_IDP_SECRET = "idp_secret";
export const STORE_KEYCLOAK_ACCOUNT = "keycloak_account";
export const STORE_KEYCLOAK_FEDERATION = "keycloak_federation";
export const STORE_OBJECT = "object";

/** Problem `error` codes (`TenantDeletionErrors`), spelled here for the same reason and pinned the same way. */
export const ERROR_SLUG_MISMATCH = "slug_mismatch";
export const ERROR_NOT_DEMO = "tenant_not_demo";
export const ERROR_NOT_SUSPENDED = "tenant_not_suspended";
export const ERROR_IS_PLATFORM_OPERATOR = "tenant_is_platform_operator";
export const ERROR_RUN_IN_PROGRESS = "demo_data_run_in_progress";
export const ERROR_REFUSED_BY_DATABASE = "tenant_refused_by_database";

/**
 * What the danger zone renders for one tenant.
 *
 *   * `none` — nothing at all. Not a disabled button and not an explanation: deleting a paying customer's
 *     tenant is not an operation this console has, and a greyed-out "Poista" over a customer would suggest
 *     it is one press away from existing.
 *   * `needsFreeze` — a demo tenant that is still live. The deletion is REFUSED server-side for it
 *     (409 `tenant_not_suspended`), and the freeze is the operator's own answer rather than a race to be
 *     won: a frozen tenant has stopped issuing tokens and stopped its background sweeps, which is what
 *     makes the purge's one transaction something other than a bet.
 *   * `ready` — a frozen demo tenant.
 */
export function deleteSectionState(tenant: {
  isDemo: boolean;
  suspendedAt?: string | null;
}): "none" | "needsFreeze" | "ready" {
  if (!tenant.isDemo) return "none";
  return (tenant.suspendedAt ?? null) === null ? "needsFreeze" : "ready";
}

/**
 * Whether what the operator typed names this tenant — matched EXACTLY, not trimmed and not case-folded,
 * which is the freeze's comparison and for the same reason: the slug is lowercase by construction, so an
 * exact match costs a careful operator nothing and is the only comparison that makes the control do what
 * its label says.
 *
 * A false here is what keeps the button disabled. The server asks the same question again (422
 * `slug_mismatch`) because this check is a courtesy and not a guard — but a console that enabled the
 * button on a near-miss would be inviting the one mistake the confirmation exists to catch: acting on the
 * tenant next to the one you meant, in a list where four customers read alike.
 */
export function slugConfirmed(typed: string, slug: string): boolean {
  return typed === slug && slug !== "";
}

/**
 * The sentence a deletion that RAN gets, by its status.
 *
 * `incomplete` is not a failure and must not read as one: the tenant and its rows are gone, and the normal
 * successful deletion lands here for about one provisioner pass (§6.2a). It gets its own sentence, and the
 * outstanding stores are named beside it rather than summarised into a number.
 */
export function deletionBannerKey(status: string): string {
  switch (status) {
    case STATUS_DONE:
      return "tenants.delete.done";
    case STATUS_INCOMPLETE:
      return "tenants.delete.incomplete";
    default:
      // A status this build does not know is reported as "it ran, go and look" rather than as either
      // ending. The tenant is gone either way; claiming the cleanup finished on the strength of a value
      // nothing here understands is the one thing this surface must not do (ADR-0013).
      return "tenants.delete.doneUnknown";
  }
}

/**
 * Which message a FAILED deletion gets, by the problem's `error` code and its status.
 *
 * Every arm but the last two is a refusal in which NOTHING was deleted, and they are told apart by CODE
 * and not by status — four of them share the 409, and the remedy differs for each: mark the tenant as a
 * demo tenant, freeze it, pick another tenant, wait for an example-dataset run to finish, or reload and
 * look at the row again.
 *
 * THE 404 IS THE ONE THAT NEEDS CARE. It does not mean the cleanup failed; it means this console has no
 * record of the id — on a first press, somebody else deleted the tenant, and on a RETRY, somebody else
 * finished the obligations (§6.3a state 4). Rendering it as a deletion error would send an operator
 * hunting for a store that nothing is waiting on.
 */
export function deletionErrorKey(status: number, problem: DeletionProblem): string {
  const code = typeof problem?.error === "string" ? problem.error : null;
  switch (code) {
    case ERROR_SLUG_MISMATCH:
      return "tenants.delete.errorSlugMismatch";
    case ERROR_NOT_DEMO:
      return "tenants.delete.errorNotDemo";
    case ERROR_NOT_SUSPENDED:
      return "tenants.delete.errorNotSuspended";
    case ERROR_IS_PLATFORM_OPERATOR:
      return "tenants.delete.errorPlatformOperator";
    case ERROR_RUN_IN_PROGRESS:
      return "tenants.delete.errorRunInProgress";
    case ERROR_REFUSED_BY_DATABASE:
      return "tenants.delete.errorRefusedByDatabase";
    default:
      return status === 404 ? "tenants.delete.error404" : "tenants.delete.error";
  }
}

/**
 * The label for one obligation store.
 *
 * The STORE is named rather than counted into a total, which is ADR-0023 §6.2a's requirement on this
 * surface: "4 jäljellä" tells the operator to press something, and for the federation row pressing is
 * exactly wrong. A store this build does not know still renders, as its own line — the count and the last
 * error are what the operator takes to whoever can act on it, and withholding them because the label is
 * unknown would lose the whole of the report.
 */
export function storeKey(store: string): string {
  switch (store) {
    case STORE_TRANSIT_KEY:
      return "tenants.delete.store.transit_key";
    case STORE_IDP_SECRET:
      return "tenants.delete.store.idp_secret";
    case STORE_KEYCLOAK_ACCOUNT:
      return "tenants.delete.store.keycloak_account";
    case STORE_KEYCLOAK_FEDERATION:
      return "tenants.delete.store.keycloak_federation";
    case STORE_OBJECT:
      return "tenants.delete.store.object";
    default:
      return "tenants.delete.store.unknown";
  }
}

/**
 * Whether pressing the button again would DO anything — the one decision that keeps this surface honest
 * about what it can and cannot perform.
 *
 * False for the normal `incomplete`: the only thing outstanding is the realm IdP alias, which the
 * provisioning component removes on its next pass (`Provisioner__IntervalSeconds`, default 30) and which
 * this console structurally cannot touch. A "Yritä uudelleen" over that row would tell the operator to
 * re-run a step this surface does not perform, and the retry would answer `incomplete` again with the same
 * row — which reads as a cleanup that is stuck when it is merely waiting.
 *
 * True as soon as any OTHER store is owed, including beside the federation row: those are the console's own
 * post-commit steps, and the retry is their remedy.
 *
 * An empty list is false, which is {@link STATUS_DONE}'s shape and needs no button either.
 */
export function consoleCanRetry(outstanding: readonly Outstanding[] | null | undefined): boolean {
  return (outstanding ?? []).some((row) => row.store !== STORE_KEYCLOAK_FEDERATION);
}

/**
 * Whether the deletion is waiting only on the provisioning component — the sentence that goes where the
 * retry button would otherwise be, so that "nothing for you to do here" is said rather than implied by a
 * missing control.
 */
export function waitingOnProvisioner(
  status: string,
  outstanding: readonly Outstanding[] | null | undefined,
): boolean {
  const rows = outstanding ?? [];
  return status === STATUS_INCOMPLETE && rows.length > 0 && !consoleCanRetry(rows);
}

/**
 * How many obligations are outstanding in total. Rendered BESIDE the per-store lines and never instead of
 * them (see {@link storeKey}); it is the number the operator reads to know whether the last retry moved
 * anything at all.
 *
 * `Number` because the generated contract types an `int32` as `number | string` — the JSON carries a
 * number, and a string that is not one would otherwise make this `NaN` silently.
 */
export function outstandingTotal(outstanding: readonly Outstanding[] | null | undefined): number {
  return (outstanding ?? []).reduce((sum, row) => {
    const count = Number(row.count);
    return sum + (Number.isFinite(count) ? count : 0);
  }, 0);
}

/**
 * The deletions list groups one summary row as `done` or `incomplete` — the label beside the slug of a
 * tenant that no longer exists.
 *
 * An unknown status is grouped with `incomplete` rather than with `done`, which is the safe direction for
 * the same reason {@link deletionBannerKey} refuses to claim either: a row this build cannot read is a row
 * nobody should take as settled.
 */
export function deletionSettled(status: string): boolean {
  return status === STATUS_DONE;
}
